import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Globe, Leaf, LogOut, Search, Settings, Users } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '@/contexts/AuthContext';
import { api, Tool, NewsItem } from '@/services/api';
import { getIcon } from '@/lib/icons';

const NEWS_PAGE_SIZE = 3;

// Moteurs de la barre de recherche ; Entrée relance le dernier utilisé (mémorisé dans le navigateur)
type SearchEngine = 'ecosia' | 'google';
const SEARCH_ENGINES: Record<SearchEngine, { label: string; url: string }> = {
  ecosia: { label: 'Ecosia', url: 'https://www.ecosia.org/search?q=' },
  google: { label: 'Google', url: 'https://www.google.fr/search?q=' },
};
const SEARCH_ENGINE_KEY = 'dashboard-search-engine';

function readSearchEngine(): SearchEngine {
  try {
    const saved = localStorage.getItem(SEARCH_ENGINE_KEY);
    return saved === 'google' || saved === 'ecosia' ? saved : 'ecosia';
  } catch {
    return 'ecosia';
  }
}
const ESSENTIALS_COUNT = 3;

// « 2026.08.20.01 » → « 20 août 2026 » : la date lisible est dérivée du numéro de version
function versionDate(version: string): string {
  const [year, month, day] = version.split('.').map(Number);
  if (!year || !month || !day) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

function formatNow(date: Date): string {
  const day = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
  const time = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${day.charAt(0).toUpperCase()}${day.slice(1)} · ${time}`;
}

function timeAgo(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'hier' : `il y a ${days} j`;
}

// Réordonne les outils selon l'ordre sauvegardé dans le navigateur.
// Les outils sans position sauvegardée (nouveaux) sont ajoutés à la fin.
function applySavedOrder(list: Tool[], storageKey: string | null): Tool[] {
  if (!storageKey) return list;
  try {
    const raw = localStorage.getItem(storageKey);
    const savedIds: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(savedIds)) return list;

    const byId = new Map(list.map((t) => [t.id, t]));
    const ordered: Tool[] = [];
    for (const id of savedIds) {
      const tool = byId.get(id as number);
      if (tool) {
        ordered.push(tool);
        byId.delete(id as number);
      }
    }
    for (const tool of list) {
      if (byId.has(tool.id)) ordered.push(tool);
    }
    return ordered;
  } catch {
    return list;
  }
}

// Nombre d'ouvertures par outil, sur la semaine en cours et la précédente.
// La semaine passée sert à garder un classement stable le lundi matin.
interface UsageStore {
  week: string;
  counts: Record<string, number>;
  previous: Record<string, number>;
}

function isoWeekKey(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

function asCounts(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object') return {};
  const result: Record<string, number> = {};
  for (const [id, count] of Object.entries(value as Record<string, unknown>)) {
    if (typeof count === 'number' && Number.isFinite(count)) result[id] = count;
  }
  return result;
}

function readUsage(storageKey: string | null): UsageStore {
  const week = isoWeekKey(new Date());
  const empty: UsageStore = { week, counts: {}, previous: {} };
  if (!storageKey) return empty;

  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return empty;

    const stored = parsed as Partial<UsageStore>;
    if (stored.week === week) {
      return { week, counts: asCounts(stored.counts), previous: asCounts(stored.previous) };
    }

    // La semaine a changé : les compteurs de la semaine écoulée deviennent l'historique,
    // et au-delà de deux semaines sans usage on repart de zéro.
    const lastWeek = isoWeekKey(new Date(Date.now() - 7 * 86_400_000));
    const rotated: UsageStore =
      stored.week === lastWeek ? { week, counts: {}, previous: asCounts(stored.counts) } : empty;

    // Persisté tout de suite : sinon la rotation se rejouerait à chaque
    // chargement à partir des mêmes compteurs, gardant en tête un outil abandonné.
    try {
      localStorage.setItem(storageKey, JSON.stringify(rotated));
    } catch {
      /* localStorage indisponible — on ignore */
    }
    return rotated;
  } catch {
    return empty;
  }
}

// Un outil ouvert cette semaine pèse plus que le même outil la semaine dernière
function usageScore(usage: UsageStore, toolId: number): number {
  return (usage.counts[toolId] ?? 0) * 2 + (usage.previous[toolId] ?? 0);
}

// Sans historique d'usage, la description de l'outil est plus utile qu'un compteur vide
function usageLabel(usage: UsageStore, tool: Tool): string {
  const count = usage.counts[tool.id] ?? 0;
  if (count > 0) return `${count} ouverture${count > 1 ? 's' : ''} cette semaine`;
  if ((usage.previous[tool.id] ?? 0) > 0) return 'Ouvert la semaine dernière';
  return tool.description || 'Pas encore ouvert';
}

// Grande pastille : un des outils les plus ouverts, avec son compteur
function EssentialPill({
  tool,
  label,
  onOpen,
}: {
  tool: Tool;
  label: string;
  onOpen: (tool: Tool) => void;
}) {
  const IconComponent = getIcon(tool.icon);

  return (
    <button
      type="button"
      onClick={() => onOpen(tool)}
      style={{ '--c': tool.color } as React.CSSProperties}
      className="kit-glass kit-press rounded-full flex items-center gap-3 pl-2.5 pr-4 py-2.5 min-w-0 text-left cursor-pointer"
    >
      <span className="kit-dot w-11 h-11 rounded-full flex items-center justify-center shrink-0">
        <IconComponent className="w-5 h-5" />
      </span>
      <span className="min-w-0">
        <span className="block font-bold text-[13.5px] leading-tight truncate">{tool.title}</span>
        <span className="block kit-teal text-[11px] font-medium truncate mt-0.5">{label}</span>
      </span>
    </button>
  );
}

// Pastille compacte, réordonnable par glisser-déposer. Le seuil du capteur
// distingue le clic (ouvre) du déplacement (réorganise).
function ToolPill({ tool, onOpen }: { tool: Tool; onOpen: (tool: Tool) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tool.id,
  });

  const IconComponent = getIcon(tool.icon);

  const style = {
    '--c': tool.color,
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.9 : 1,
  } as React.CSSProperties;

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={() => onOpen(tool)}
      style={style}
      className={`kit-glass kit-press rounded-full flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 min-w-0 text-left touch-none ${
        isDragging ? 'cursor-grabbing' : 'cursor-pointer'
      }`}
      {...attributes}
      {...listeners}
    >
      <span className="kit-dot w-7 h-7 rounded-full flex items-center justify-center shrink-0">
        <IconComponent className="w-3.5 h-3.5" />
      </span>
      <span className="font-semibold text-[12.5px] leading-tight truncate">{tool.title}</span>
    </button>
  );
}

// Bulle d'actu : source et date en tête, titre en grand sur trois lignes
function NewsPill({ item }: { item: NewsItem }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="kit-glass kit-press rounded-[22px] flex flex-col gap-2 px-4 py-3.5 min-h-[112px] min-w-0 cursor-pointer no-underline text-inherit"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="kit-src shrink-0">{item.source}</span>
        <span className="text-[11px] text-[#8c98a4] whitespace-nowrap shrink-0">{timeAgo(item.publishedAt)}</span>
      </span>
      <span className="kit-clamp-3 font-semibold text-[14.5px] leading-snug">{item.title}</span>
    </a>
  );
}

export function DashboardPage() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [news, setNews] = useState<NewsItem[] | null>(null);
  const [newsError, setNewsError] = useState(false);
  const [newsPage, setNewsPage] = useState(0);
  const [usage, setUsage] = useState<UsageStore>(() => readUsage(null));
  const [query, setQuery] = useState('');
  const [lastEngine, setLastEngine] = useState<SearchEngine>(readSearchEngine);
  const [now, setNow] = useState(() => new Date());

  const { token, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Les moteurs interdisent l'affichage de leurs résultats dans une page tierce :
  // la recherche part donc dans un nouvel onglet, le dashboard reste ouvert.
  const runSearch = (engine: SearchEngine) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    window.open(`${SEARCH_ENGINES[engine].url}${encodeURIComponent(trimmed)}`, '_blank', 'noopener,noreferrer');
    setLastEngine(engine);
    try {
      localStorage.setItem(SEARCH_ENGINE_KEY, engine);
    } catch {
      /* localStorage indisponible — on ignore */
    }
  };

  // Entrée relance le dernier moteur utilisé
  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    runSearch(lastEngine);
  };

  // Ordre et usage gardés dans le navigateur (par utilisateur) — aucune base de données
  const orderKey = user ? `dashboard-tool-order-${user.id}` : null;
  const usageKey = user ? `dashboard-tool-usage-${user.id}` : null;

  const sensors = useSensors(
    // Seuil de 8px : un simple clic ouvre l'outil, un déplacement réorganise
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setUsage(readUsage(usageKey));
  }, [usageKey]);

  useEffect(() => {
    const loadTools = async () => {
      if (!token) return;

      try {
        const { tools: fetchedTools } = await api.tools.getAll(token);
        setTools(applySavedOrder(fetchedTools, orderKey));
      } catch {
        setError('Erreur lors du chargement des outils');
      } finally {
        setIsLoading(false);
      }
    };

    loadTools();
  }, [token, orderKey]);

  useEffect(() => {
    const loadNews = async () => {
      if (!token) return;

      try {
        const { items } = await api.news.getAll(token);
        setNews(items);
      } catch {
        setNewsError(true);
      }
    };

    loadNews();
  }, [token]);

  const openTool = (tool: Tool) => {
    window.open(tool.url, '_blank', 'noopener,noreferrer');

    setUsage((current) => {
      const updated: UsageStore = {
        ...current,
        counts: { ...current.counts, [tool.id]: (current.counts[tool.id] ?? 0) + 1 },
      };
      if (usageKey) {
        try {
          localStorage.setItem(usageKey, JSON.stringify(updated));
        } catch {
          /* localStorage indisponible — on ignore */
        }
      }
      return updated;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setTools((current) => {
      const oldIndex = current.findIndex((t) => t.id === active.id);
      const newIndex = current.findIndex((t) => t.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return current;

      const reordered = arrayMove(current, oldIndex, newIndex);
      if (orderKey) {
        try {
          localStorage.setItem(orderKey, JSON.stringify(reordered.map((t) => t.id)));
        } catch {
          /* localStorage indisponible — on ignore */
        }
      }
      return reordered;
    });
  };

  // Classement par usage ; sans historique, l'ordre choisi par glisser-déposer fait référence
  const rankedTools = useMemo(() => {
    return tools
      .map((tool, index) => ({ tool, index, score: usageScore(usage, tool.id) }))
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .map((entry) => entry.tool);
  }, [tools, usage]);

  const essentials = rankedTools.slice(0, ESSENTIALS_COUNT);
  const secondary = rankedTools.slice(ESSENTIALS_COUNT);

  const newsPages = useMemo(() => {
    if (!news) return [];
    const pages: NewsItem[][] = [];
    for (let i = 0; i < news.length; i += NEWS_PAGE_SIZE) {
      pages.push(news.slice(i, i + NEWS_PAGE_SIZE));
    }
    return pages;
  }, [news]);

  const currentNewsPage = newsPages[Math.min(newsPage, Math.max(newsPages.length - 1, 0))] ?? [];

  if (isLoading) {
    return (
      <div className="kit-surface flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36a9e1]"></div>
      </div>
    );
  }

  return (
    <div className="kit-surface h-full overflow-y-auto">
      <main className="max-w-4xl w-full mx-auto px-4 sm:px-6 py-5 sm:py-7">
        {/* Recherche web : Ecosia ou Google au choix à l'envoi, roue crantée ronde à droite */}
        <div className="flex items-center gap-3">
          <form
            onSubmit={handleSearch}
            className="kit-glass rounded-[22px] sm:rounded-full flex flex-wrap sm:flex-nowrap items-center gap-2 pl-4 pr-1.5 py-1.5 sm:py-0 sm:h-11 flex-1 min-w-0"
          >
            <Search className="w-4 h-4 text-[#8c98a4] shrink-0" />
            <input
              id="dashboard-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher sur le web"
              aria-label="Rechercher sur le web"
              className="flex-1 basis-[calc(100%-24px)] sm:basis-auto min-w-0 h-8 bg-transparent border-0 outline-none text-[13.5px] text-[#e8eef2] placeholder:text-[#8c98a4]"
            />
            <button
              type="button"
              onClick={() => runSearch('ecosia')}
              title="Rechercher sur Ecosia"
              className={`kit-eco kit-engine ${lastEngine === 'ecosia' ? 'kit-last' : ''} h-8 px-2.5 sm:px-3.5 rounded-full flex items-center justify-center gap-1.5 basis-[calc(50%-4px)] sm:basis-auto shrink-0 cursor-pointer text-[12px] sm:text-[13px] font-semibold`}
            >
              <Leaf className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.4} />
              Ecosia
            </button>
            <button
              type="button"
              onClick={() => runSearch('google')}
              title="Rechercher sur Google"
              className={`kit-go kit-engine ${lastEngine === 'google' ? 'kit-last' : ''} h-8 px-2.5 sm:px-3.5 rounded-full flex items-center justify-center gap-1.5 basis-[calc(50%-4px)] sm:basis-auto shrink-0 cursor-pointer text-[12px] sm:text-[13px] font-semibold`}
            >
              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.6} />
              Google
            </button>
          </form>

          {/* Roue crantée : menu compte (utilisateur connecté, paramètres, gestion, déconnexion) */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Compte et paramètres"
                className="kit-glass kit-press w-11 h-11 rounded-full flex items-center justify-center shrink-0 cursor-pointer"
              >
                <Settings className="w-[18px] h-[18px] text-[#e8eef2]" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="kit-menu w-72 bg-[#1a2128] border-white/15 text-[#e8eef2]" align="end" side="bottom">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.email}</p>
                <p className="text-xs text-[#8c98a4]">
                  {user?.firstName} {user?.lastName}
                </p>
              </div>
              <Separator className="my-2 bg-white/10" />
              {user?.isAdmin && (
                <button
                  className="kit-menu-item flex w-full items-center gap-2 px-2 py-1.5 text-sm rounded-sm"
                  onClick={() => navigate('/settings')}
                >
                  <Settings className="w-4 h-4" />
                  Paramètres
                </button>
              )}
              <button
                className="kit-menu-item flex w-full items-center gap-2 px-2 py-1.5 text-sm rounded-sm"
                onClick={() => navigate('/users')}
              >
                <Users className="w-4 h-4" />
                Gestion des utilisateurs
              </button>
              <Separator className="my-2 bg-white/10" />
              <button
                className="kit-menu-item flex w-full items-center gap-2 px-2 py-1.5 text-sm text-red-400 rounded-sm"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </button>
              <Separator className="my-2 bg-white/10" />
              <div className="px-2 text-[11px] text-[#8c98a4] leading-snug">
                <p>Version du {versionDate(__APP_VERSION__)}</p>
                <p>v{__APP_VERSION__}</p>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex justify-between items-baseline gap-3 flex-wrap mt-5 mb-4 px-0.5">
          <h2 className="font-bold text-[20px] leading-tight">
            Bonjour{user?.firstName ? `, ${user.firstName}` : ''}
          </h2>
          <p className="text-[11px] text-[#8c98a4]">
            {formatNow(now)} · v{__APP_VERSION__}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 text-sm font-semibold text-red-300 bg-red-500/15 border border-red-400/30 rounded-xl">
            {error}
          </div>
        )}

        {tools.length === 0 && !error && (
          <div className="text-center py-10">
            <Globe className="w-12 h-12 text-[#8c98a4] mx-auto mb-4" />
            <h3 className="text-lg font-bold">Aucun outil disponible</h3>
            <p className="text-[#8c98a4] mt-1">Les outils seront ajoutés prochainement</p>
          </div>
        )}

        {/* Grille à colonnes égales : les pastilles s'alignent en rangées nettes */}
        {essentials.length > 0 && (
          <section aria-label="Vos outils les plus utilisés">
            <p className="kit-label mb-2.5">Vos essentiels</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {essentials.map((tool) => (
                <EssentialPill key={tool.id} tool={tool} label={usageLabel(usage, tool)} onOpen={openTool} />
              ))}
            </div>
          </section>
        )}

        {secondary.length > 0 && (
          <section className="mt-6" aria-label="Toutes les applications">
            <p className="kit-label mb-2.5">Toutes les applications</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={secondary.map((t) => t.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {secondary.map((tool) => (
                    <ToolPill key={tool.id} tool={tool} onOpen={openTool} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </section>
        )}

        {/* Actus IA (source paramétrable via NEWS_FEEDS côté serveur) */}
        <section className="mt-6" aria-label="Actualités IA">
          <p className="kit-label mb-2.5">Actus IA</p>

          {news === null && !newsError && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {Array.from({ length: NEWS_PAGE_SIZE }, (_, i) => (
                <div key={i} className="kit-glass rounded-[22px] h-[112px] animate-pulse" />
              ))}
            </div>
          )}

          {(newsError || (news !== null && news.length === 0)) && (
            <p className="text-[12.5px] text-[#8c98a4] py-3">Actualités indisponibles pour le moment</p>
          )}

          {news !== null && news.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {currentNewsPage.map((item) => (
                  <NewsPill key={item.url} item={item} />
                ))}
              </div>
              {newsPages.length > 1 && (
                <div className="flex justify-center gap-2 mt-3.5">
                  {newsPages.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      aria-label={`Page d'actualités ${index + 1}`}
                      onClick={() => setNewsPage(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === newsPage
                          ? 'bg-[#36a9e1] shadow-[0_0_8px_#36a9e1]'
                          : 'bg-white/20 hover:bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
